// @ts-nocheck
// app/fields.tsx — Editorial fields directory

import { useMemo, useState, useCallback } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';
import { ArrowLeft, Search } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section, useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { trackTopicClick } from '@/utils/analytics';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const SANS_MED = theme.fonts.medium;
const SANS_BOLD = theme.fonts.bold;
const IS_WEB = Platform.OS === 'web';

const FIELDS = [
  { key: 'software',     title: 'תכנות ופיתוח',           desc: 'Full-Stack · Web · Mobile · Backend',     catalogTopic: 'תכנות',          examples: '2,140+ קורסים', count: '01' },
  { key: 'ai',           title: 'בינה מלאכותית',           desc: 'AI tools · ML · Prompt · אוטומציות',      catalogTopic: 'בינה מלאכותית',  examples: '380+ קורסים',   count: '02' },
  { key: 'data',         title: 'דאטה ואנליטיקה',          desc: 'SQL · BI · Data Science · Python',       catalogTopic: 'דאטה',            examples: '720+ קורסים',   count: '03' },
  { key: 'cyber',        title: 'סייבר ואבטחת מידע',       desc: 'SOC · PenTest · Network · CompTIA',      catalogTopic: 'סייבר',          examples: '460+ קורסים',   count: '04' },
  { key: 'marketing',    title: 'שיווק דיגיטלי',           desc: 'Meta/Google Ads · SEO · תוכן',            catalogTopic: 'שיווק דיגיטלי',  examples: '950+ קורסים',   count: '05' },
  { key: 'product',      title: 'ניהול מוצר',              desc: 'PM · Growth · Roadmap · UX בסיסי',       catalogTopic: 'ניהול מוצר',     examples: '210+ קורסים',   count: '06' },
  { key: 'ux',           title: 'UI/UX ועיצוב מוצר',       desc: 'Figma · Design Systems · מחקר',           catalogTopic: 'UI UX',           examples: '340+ קורסים',   count: '07' },
  { key: 'finance',      title: 'שוק ההון ופיננסים',       desc: 'מסחר · השקעות · חשבונאות',                catalogTopic: 'שוק ההון',       examples: '180+ קורסים',   count: '08' },
  { key: 'psychometric', title: 'פסיכומטרי ומכינות',       desc: 'כמותי · מילולי · אנגלית',                 catalogTopic: 'פסיכומטרי',       examples: '90+ קורסים',    count: '09' },
  { key: 'sales',        title: 'מכירות ו-CRM',            desc: 'BDR/SDR · תסריטים · מערכות',              catalogTopic: 'מכירות',          examples: '120+ קורסים',   count: '10' },
];

export default function FieldsScreen() {
  const router = useRouter();
  const { isDesktop } = useEditorialBreakpoint();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return FIELDS;
    return FIELDS.filter(f => (f.title + ' ' + f.desc).toLowerCase().includes(s));
  }, [q]);

  const goCatalog = useCallback((field: typeof FIELDS[number]) => {
    trackTopicClick(field.catalogTopic);
    router.push({ pathname: '/catalog' as any, params: { topic: encodeURIComponent(field.catalogTopic) } as any });
  }, [router]);

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>תחומי לימוד · Kursi</title>
          <meta name="description" content="עשרה תחומי לימוד. אלפי קורסים. שלוש התאמות בקליק. בחרו תחום וקבלו את הקטלוג המסונן." />
          <link rel="canonical" href="https://kursiaii.com/fields" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1240}>
          <PageHero
            kicker="גיליון התחומים · עיתון Kursi"
            title={<>עשרה כיוונים{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>שכל אחד שלם.</Text></>}
            lede="בחרו תחום — ונקפיץ אתכם ישר לקטלוג המסונן. לא בטוחים? כתבו מילה בחיפוש."
            meta={`${FIELDS.length} תחומים פעילים · מתעדכן יומית`}
          />

          {/* Search bar */}
          <View style={s.searchBlock}>
            <Search color={INK} size={18} />
            <TextInput
              value={q}
              onChangeText={setQ}
              placeholder="חיפוש תחום — לדוגמה AI, פסיכומטרי, סייבר"
              placeholderTextColor={INK_MUTED}
              style={s.searchInput}
              accessibilityLabel="חיפוש תחומים"
            />
          </View>
        </Section>

        {/* ─── Grid ────────────────────────────────────────────────── */}
        <Section py="md" px="md" max={1240}>
          <View style={s.grid}>
            {filtered.map((f, i) => (
              <Pressable
                key={f.key}
                onPress={() => goCatalog(f)}
                style={({ hovered, pressed }: any) => [
                  s.card,
                  { width: isDesktop ? '50%' : '100%' },
                  hovered && s.cardHover,
                  pressed && { opacity: 0.92 },
                ]}
                accessibilityRole="link"
                accessibilityLabel={`קטלוג ${f.title}`}
              >
                <View style={s.cardInner}>
                  <View style={{ flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'flex-start' } as any}>
                    <Numeral size={88}>{f.count}</Numeral>
                    <View style={{ alignItems: 'flex-start', gap: 6 } as any}>
                      <Text style={s.cardMeta}>{f.examples}</Text>
                      <ArrowLeft color={INK} size={20} />
                    </View>
                  </View>
                  <View style={{ height: 18 }} />
                  <Text style={s.cardTitle}>{f.title}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.cardDesc}>{f.desc}</Text>
                </View>
              </Pressable>
            ))}
          </View>

          {filtered.length === 0 && (
            <View style={s.empty}>
              <Eyebrow color={ACCENT}>לא נמצא תחום</Eyebrow>
              <View style={{ height: 14 }} />
              <Headline level={2}>תכתבו לנו מה אתם מחפשים.</Headline>
              <View style={{ height: 14 }} />
              <Text style={s.emptyBody}>נסו מילה אחרת, או עברו ישר לקטלוג ובצעו חיפוש שם.</Text>
              <View style={{ height: 22 }} />
              <View style={{ flexDirection: 'row-reverse', gap: 14 } as any}>
                <EditorialButton label="כל הקטלוג" onPress={() => router.push('/catalog')} variant="primary" trailing={<ArrowLeft size={14} color={PAPER} />} />
                <EditorialButton label="התחלת התאמה" onPress={() => router.push('/chat')} variant="secondary" />
              </View>
            </View>
          )}
        </Section>

        <Section py="md" px="md" max={1240}><DoubleRule /></Section>

        <Section py="xxl" px="md" max={1100}>
          <View style={{ alignItems: 'center' }}>
            <Headline level={2} align="center">לא יודעים מאיפה להתחיל?</Headline>
            <View style={{ height: 14 }} />
            <Text style={[s.lede, { textAlign: 'center' }]}>שאלון התאמה של דקה. שלושה כיוונים. אפס מחויבות.</Text>
            <View style={{ height: 24 }} />
            <EditorialButton label="לשאלון ההתאמה" onPress={() => router.push('/quiz')} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
          </View>
        </Section>

        <EditorialFooter
          columns={[
            { title: 'מקטעים', items: [
              { label: 'הקטלוג המלא', href: '/catalog' },
              { label: 'שאלון התאמה', href: '/quiz' },
              { label: 'מדריכי בחירה', href: '/guides' },
            ]},
            { title: 'המוצר', items: [
              { label: 'איך זה עובד', href: '/how-it-works' },
              { label: 'שאלות נפוצות', href: '/faq' },
              { label: 'אודות', href: '/about' },
            ]},
          ]}
          onNavigate={(href) => router.push(href as any)}
        />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  searchBlock: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
    borderWidth: 1, borderColor: INK, borderRadius: 0,
    paddingHorizontal: 18, paddingVertical: 14,
    backgroundColor: PAPER_SOFT,
    marginTop: 12,
  } as any,
  searchInput: {
    flex: 1, fontFamily: SANS_MED, fontSize: 17, color: INK,
    textAlign: 'right', backgroundColor: 'transparent',
    ...(IS_WEB ? { outlineStyle: 'none' } as any : {}),
  },
  grid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 0 } as any,
  card: {
    padding: 0,
    marginLeft: -1, marginTop: -1,
  } as any,
  cardInner: {
    borderWidth: 1, borderColor: INK,
    padding: 28,
    backgroundColor: PAPER,
    ...(IS_WEB ? { transition: 'background 200ms ease, color 200ms ease' } as any : {}),
  },
  cardHover: {},
  cardMeta: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.5, textTransform: 'uppercase' as any },
  cardTitle: { fontFamily: SERIF_BOLD, fontSize: 30, color: INK, letterSpacing: -0.7, textAlign: 'right', lineHeight: 36 },
  cardDesc: { fontFamily: SANS, fontSize: 15, color: INK_SOFT, lineHeight: 24, textAlign: 'right' },

  empty: { padding: 40, alignItems: 'center' },
  emptyBody: { fontFamily: SANS, fontSize: 16, color: INK_SOFT, textAlign: 'center', maxWidth: 480, lineHeight: 26 },
  lede: { fontFamily: SERIF, fontSize: 19, color: INK_SOFT, lineHeight: 30, maxWidth: 600 },
});
